import { UserProfileComponentProps } from '@components/pages/home/components/home-user-profile-modal.component.tsx';
import { FormLabel } from '@components/shared/form-label.component';
import { Input } from '@components/ui/input';
import { cn } from '@lib/utils';
import { useSearchUserProfiles } from '@queries/user-profile.query';
import { referrerStepSchema } from '@schemas/user-profile.schema';
import { CircleAlertIcon, CircleCheckIcon } from 'lucide-react';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

interface ReferrerStepProps extends UserProfileComponentProps {
  isReferrerDisabled?: boolean;
}

type AsyncValidationStatus = 'idle' | 'loading' | 'success' | 'error';

interface AsyncValidationState {
  status: AsyncValidationStatus;
  message?: string;
}

export const ReferrerStep: FunctionComponent<ReferrerStepProps> = (props) => {
  const { formData, handleChange, isReferrerDisabled } = props;

  const [debouncedReferrerCode, setDebouncedReferrerCode] = useState(formData.referrerCode);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferrerCode(formData.referrerCode || '');
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.referrerCode]);

  const result = referrerStepSchema.safeParse({ referrerCode: debouncedReferrerCode });
  const { referrerCode } = result.success ? result.data : {};

  const { data: foundProfiles, isLoading: isSearching, status: queryStatus } = useSearchUserProfiles(referrerCode);

  const validationState = useMemo((): AsyncValidationState | null => {
    const currentCode = formData.referrerCode || '';

    if (!currentCode) {
      return null;
    }

    if (!referrerCode) {
      return null;
    }

    if (currentCode !== referrerCode) {
      return {
        status: 'idle',
      };
    }

    if (queryStatus === 'pending' || isSearching) {
      return {
        status: 'loading',
        message: 'Searching for referrer...',
      };
    }

    if (foundProfiles && foundProfiles.length > 0) {
      const referrer = foundProfiles[0];
      return {
        status: 'success',
        message: `Referred by ${referrer.firstName} ${referrer.lastName}`,
      };
    }

    if (queryStatus === 'success') {
      return {
        status: 'error',
        message: 'No user found with this referrer code',
      };
    }

    return null;
  }, [formData.referrerCode, referrerCode, foundProfiles, queryStatus, isSearching]);

  const inputHasSuccess = validationState?.status === 'success';
  const inputHasError = validationState?.status === 'error';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      <FormLabel
        label="Referrer Code"
        htmlFor="referrerCode"
      />
      <Input
        id="referrerCode"
        name="referrerCode"
        type="text"
        value={formData.referrerCode || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Enter referrer code (optional)"
        disabled={isReferrerDisabled}
        hasError={inputHasError}
        className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
      />
      <div
        className={cn(
          'text-xs min-h-4 flex items-center gap-1',
          validationState?.status === 'success' && 'text-green-600',
          validationState?.status === 'error' && 'text-red-600',
        )}
      >
        {validationState?.status === 'success' && <CircleCheckIcon className="h-5 w-5 fill-green-500 text-white" />}
        {validationState?.status === 'error' && <CircleAlertIcon className="h-5 w-5 fill-red-500 text-white" />}
        <p>{validationState?.status !== 'loading' && validationState?.message}</p>
      </div>
    </div>
  );
};
